import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Stack from 'react-bootstrap/Stack'
import Card from 'react-bootstrap/Card'
import { seatLookupFindEventsPackages } from '../../../../utilities/api'
import { useEffect, useState } from 'react'
import axios from 'axios'
import { formatDateTime, getTimezoneDate } from '../../../../utilities/helpers'
import { SearchBarNew } from '../../../SearchBarNew'
import { validateSeatSearch } from '@blocktickets/shared-utils'

export default function SearchCard({
  organization,
  handleFilterChange,
  triggerSeatSearch,
  selectedEventOrPackage,
  sourceFilters,
  setSourceFilters,
  isLoading,
}) {
  const [searchType, setSearchType] = useState('general')
  const [searchTerm, setSearchTerm] = useState('')
  const [sectionValue, setSectionValue] = useState('')
  const [rowValue, setRowValue] = useState('')
  const [seatValue, setSeatValue] = useState('')

  const toggleButtonFilter = (filter) => {
    setSourceFilters((prevFilters) =>
      prevFilters.map((f) =>
        f.value === filter.value ? { ...f, selected: !f.selected } : f
      )
    )
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (searchType === 'seat') {
      const section = sectionValue.trim()
      const row = rowValue.trim()
      const seat = seatValue.trim()

      const seatValidation = validateSeatSearch({ section, row, seat })
      if (!seatValidation.valid) {
        alert(seatValidation.message)
        return
      }

      triggerSeatSearch({
        seatSearch: true,
        section: section || undefined,
        row: row || undefined,
        seat: seat || undefined,
      })
    } else {
      triggerSeatSearch({ seatSearch: false, searchTerm: searchTerm })
    }
  }

  // Clean up values when changing searchType
  useEffect(() => {
    setSearchTerm('')
    setSectionValue('')
    setRowValue('')
    setSeatValue('')
  }, [searchType])

  return (
    <Card body className="card--md">
      <Stack gap={4}>
        <Form onSubmit={handleSubmit}>
          <Stack gap={3}>
            <Stack direction="horizontal" gap={5} className="align-items-end">
              <Stack style={{ maxWidth: 468 }}>
                <span className="form-label" id="filter-by-orders">
                  FilterEventPackageSelect
                </span>
                <EventPackageSelect
                  selectedEventOrPackage={selectedEventOrPackage}
                  handleFilterChange={handleFilterChange}
                  organization={organization}
                />
              </Stack>
              <Stack className="flex-fill">
                <span className="form-label">Search Type</span>
                <Form.Select
                  value={searchType}
                  onChange={(e) => setSearchType(e.target.value)}
                  className="rounded-md"
                >
                  <option value="general">
                    General Search (Name, Email, Order Number)
                  </option>
                  <option value="seat">Seat Location Search</option>
                </Form.Select>
              </Stack>
            </Stack>

            {searchType === 'general' ? (
              <Stack direction="horizontal" gap={3} className="align-items-end">
                <Stack className="flex-fill">
                  <span className="form-label">Search</span>
                  <SearchBarNew
                    onQueryChange={(value) => setSearchTerm(value)}
                    placeholder="Search by name, email, or order number"
                    size="md"
                  />
                </Stack>
                <Button
                  disabled={isLoading}
                  type="submit"
                  size="lg"
                  className="search-btn"
                >
                  Search
                </Button>
              </Stack>
            ) : (
              <Stack gap={3}>
                <Stack direction="horizontal" gap={3}>
                  <Stack className="flex-fill">
                    <Form.Label>Section</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g., C"
                      value={sectionValue}
                      onChange={(e) => setSectionValue(e.target.value)}
                      className="rounded-md"
                    />
                  </Stack>
                  <Stack className="flex-fill">
                    <Form.Label>Row</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g., N"
                      value={rowValue}
                      onChange={(e) => setRowValue(e.target.value)}
                      className="rounded-md"
                    />
                  </Stack>
                  <Stack className="flex-fill">
                    <Form.Label>Seat</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="e.g., 7"
                      value={seatValue}
                      onChange={(e) => setSeatValue(e.target.value)}
                      className="rounded-md"
                    />
                  </Stack>
                  <Button
                    disabled={isLoading}
                    type="submit"
                    size="lg"
                    className="search-btn align-self-end"
                  >
                    Search
                  </Button>
                </Stack>
                <p className="text-muted small mb-0">
                  Search by Section, or Section + Row, or Section + Row + Seat.
                  Row requires Section. Seat requires Row.
                </p>
              </Stack>
            )}
          </Stack>
        </Form>
        <Stack direction="horizontal" gap={2}>
          {sourceFilters.map((filter) => {
            return (
              <Button
                key={filter.value}
                onClick={() => toggleButtonFilter(filter)}
                variant="outline-light"
                className={`filter-btn ${filter.selected ? 'selected' : ''}`}
              >
                {filter.label}
              </Button>
            )
          })}
        </Stack>
      </Stack>
    </Card>
  )
}

function EventPackageSelect({
  selectedEventOrPackage,
  handleFilterChange,
  organization,
}) {
  const [organizationEvents, setOrganizationEvents] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const abortController = new AbortController()
    const signal = abortController.signal
    let isMounted = true

    const fetchOrganizationEventsPackages = async () => {
      try {
        setIsLoading(true)

        if (!organization?.uuid) {
          throw new Error('Organization UUID is required')
        }

        const response = await seatLookupFindEventsPackages(
          organization.uuid,
          signal
        )

        if (isMounted && response?.data) {
          const eventsPackages = Array.isArray(response.data)
            ? response.data
            : []
          setOrganizationEvents(eventsPackages)
        }
      } catch (err) {
        if (axios.isCancel(err)) {
          console.log('Fetch intentionally aborted:', err.message)
        }
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    if (organization?.uuid) {
      fetchOrganizationEventsPackages()
    } else {
      setIsLoading(false)
    }

    return () => {
      isMounted = false
      abortController.abort()
    }
  }, [organization?.uuid])

  const events = organizationEvents.filter((gp) => gp?.event) || []
  const packages = organizationEvents.filter((gp) => gp?.package) || []
  const flexPacks = organizationEvents.filter((gp) => gp?.flexPack) || []

  return (
    <Form.Select
      value={
        selectedEventOrPackage ? JSON.stringify(selectedEventOrPackage) : ''
      }
      className={`rounded-md`}
      onChange={(e) => handleFilterChange(e.target.value)}
      aria-labelledby="filter-by-orders"
      disabled={isLoading}
    >
      <option disabled={isLoading} value="">
        {isLoading ? 'Loading...' : 'All orders'}
      </option>

      {events.length > 0 && (
        <optgroup label="Events">
          {events.map((gp) => (
            <option
              key={`event-${gp.id || gp.uuid}`}
              value={JSON.stringify(gp)}
            >
              {gp.name || 'N/A'}
              {` || ` +
                formatDateTime(getTimezoneDate(gp?.start, gp?.timezone))}
            </option>
          ))}
        </optgroup>
      )}

      {packages.length > 0 && (
        <optgroup label="Packages">
          {packages.map((gp) => (
            <option
              key={`package-${gp.id || gp.uuid}`}
              value={JSON.stringify(gp)}
            >
              {gp.name || 'Unnamed Package'}
            </option>
          ))}
        </optgroup>
      )}
      {flexPacks.length > 0 && (
        <optgroup label="Flex Packages">
          {flexPacks.map((gp) => (
            <option
              key={`flex-package-${gp.id || gp.uuid}`}
              value={JSON.stringify(gp)}
            >
              {gp.name || 'Unnamed Flex Package'}
            </option>
          ))}
        </optgroup>
      )}
    </Form.Select>
  )
}
